import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Award } from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Forex Brokers Association | Official FBA Awards & Global Standards"
        description="Promoting integrity, transparency, and ethical practices in foreign exchange trading. Discover award-winning brokers and FX Global Code standards in Hong Kong."
        keywords="forex brokers, FBA, forex trading, best forex brokers 2024, forex awards, FX global code, forex association Hong Kong, trading ethics"
        url="https://forexbrokersassociation.com/"
      />

      {/* Hero Section with Hong Kong Background */}
      <div className="relative h-[700px] overflow-hidden">
        {/* Hong Kong Background Image */}
        <img 
          src="https://images.unsplash.com/photo-1690070767302-86a0f719a36f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxIb25nJTIwS29uZyUyMHNreWxpbmV8ZW58MHx8fHwxNzc2MzI5Njg1fDA&ixlib=rb-4.1.0&q=85"
          alt="Hong Kong Skyline - Forex Brokers Association Headquarters"
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        {/* Enhanced Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70"></div>
        
        {/* Hero Text on Top of Image */}
        <div className={`relative h-full flex items-center justify-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 animate-fade-in-up">
              Forex Brokers <span className="text-cyan-400 gradient-text">Association</span>
            </h1>
            <p className="text-xl lg:text-2xl text-white mb-8 max-w-3xl mx-auto leading-relaxed animate-fade-in-up stagger-2">
              Promoting integrity, transparency, and ethical practices in the foreign exchange market
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up stagger-3">
              <Link
                to="/about"
                className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-xl font-semibold shadow-depth-3 hover:shadow-depth-5 hover-glow transition-all duration-300 flex items-center justify-center space-x-2 btn-enhanced"
              >
                <span>Learn More</span>
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-white/95 backdrop-blur-sm text-gray-800 rounded-xl font-semibold shadow-depth-3 hover:shadow-depth-4 hover:bg-white transition-all duration-300"
              >
                Join Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="card-enhanced bg-white rounded-2xl p-8 shadow-depth-2 hover:shadow-depth-4 animate-scale-in stagger-1">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mb-6 shadow-depth-2">
              <Shield className="text-white" size={32} />
            </div>
            <h3 className="text-4xl font-bold text-gray-900 mb-2">100+</h3>
            <p className="text-gray-600 font-medium">Member Brokers</p>
          </div>
          <div className="card-enhanced bg-white rounded-2xl p-8 shadow-depth-2 hover:shadow-depth-4 animate-scale-in stagger-2">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mb-6 shadow-depth-2">
              <Users className="text-white" size={32} />
            </div>
            <h3 className="text-4xl font-bold text-gray-900 mb-2">50+</h3>
            <p className="text-gray-600 font-medium">Countries Represented</p>
          </div>
          <div className="card-enhanced bg-white rounded-2xl p-8 shadow-depth-2 hover:shadow-depth-4 animate-scale-in stagger-3">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mb-6 shadow-depth-2">
              <Award className="text-white" size={32} />
            </div>
            <h3 className="text-4xl font-bold text-gray-900 mb-2">15+</h3>
            <p className="text-gray-600 font-medium">Annual Awards</p>
          </div>
        </div>
        </div>
      </div>

      {/* Ethics Section */}
      <div className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <span className="text-cyan-500 font-semibold text-sm uppercase tracking-wider">Ethics</span>
            <h2 className="text-5xl font-bold text-gray-900 mt-3 mb-6">LEADING PRINCIPLE</h2>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Market Participants are expected to behave in an ethical and professional manner 
              to promote the fairness and integrity of the FX Market.
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-3xl p-10 lg:p-16 shadow-depth-3 card-enhanced animate-fade-in-up stagger-2">
            <p className="text-gray-700 text-lg leading-relaxed">
              The ethical and professional behavior of Market Participants underpins the fairness 
              and integrity of the FX Market. The exercise of judgement is central to acting ethically 
              and professionally, and Market Participants (meaning both firms and their personnel) should 
              be guided in doing so by the high-level principles below, both when applying the specific 
              guidance in the Global Code and at all times when participating in the FX Market.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative bg-gradient-to-r from-cyan-500 via-cyan-600 to-blue-600 py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8 animate-fade-in-up">
            Ready to Join Our Community?
          </h2>
          <p className="text-xl text-cyan-50 mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up stagger-2">
            Become a member and contribute to the highest standards of ethics in forex trading
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center px-10 py-5 bg-white text-cyan-600 rounded-xl font-bold shadow-depth-4 hover:shadow-depth-5 hover-glow transition-all duration-300 space-x-3 btn-enhanced text-lg animate-scale-in stagger-3"
          >
            <span>Get in Touch</span>
            <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;